// src/screens/Playbooks.tsx
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogoLoader } from "../components/LogoLoader";
import { supabase } from "../supabaseClient";
import { Book, PlusCircle, Pencil, Trash2, Search, Filter } from "lucide-react";
import i18n from "../i18n";
import { BackButton } from "../components/BackButton";

type TeamRow = { id: string; team_name: string };

type PlaybookRow = {
  id: string;
  name: string;
  description?: string | null;
  created_at: string;
  team_id: string;
  team?: { team_name: string } | null;
};

/* ===== i18n mini-hook ===== */
function useI18nSafe() {
  const [lng, setLng] = useState(
    (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase()
  );
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = (key: string, options?: Record<string, unknown>): string =>
    options != null ? String((i18n.t as any)(key, options)) : String((i18n.t as any)(key));
  return { t, lng };
}

export const Playbooks = () => {
  const { t, lng } = useI18nSafe();

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<PlaybookRow[]>([]);
  const [teams, setTeams] = useState<TeamRow[]>([]);
  const [teamFilter, setTeamFilter] = useState<string>(""); // "" = toutes
  const [query, setQuery] = useState("");
  const [deleteId, setDeleteId] = useState<string | null>(null); // modale
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { setLoading(false); return; }

        const { data: teamRows } = await supabase
          .from("teams")
          .select("id, team_name")
          .eq("user_id", user.id)
          .order("team_name", { ascending: true });
        setTeams(teamRows || []);

        const { data: playbooks } = await supabase
          .from("playbooks")
          .select(`id, name, description, created_at, team_id, teams:teams ( team_name )`)
          .order("created_at", { ascending: false });

        const normalized: PlaybookRow[] = (playbooks || []).map((pb: any) => ({
          id: pb.id,
          name: pb.name,
          description: pb.description,
          created_at: pb.created_at,
          team_id: pb.team_id,
          team: Array.isArray(pb.teams) ? (pb.teams[0] ?? null) : (pb.teams ?? null),
        }));

        setRows(normalized);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const fmtDate = (iso: string) => {
    const locale = lng === "fr" ? "fr-FR" : "en-US";
    return new Date(iso).toLocaleDateString(locale, {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter(r => {
      const byName = !q || r.name.toLowerCase().includes(q);
      const byTeam = !teamFilter || r.team_id === teamFilter;
      return byName && byTeam;
    });
  }, [rows, query, teamFilter]);

  const confirmDelete = (id: string) => setDeleteId(id);

  const doDelete = async () => {
    if (!deleteId) return;
    setLoading(true);
    try {
      await supabase.from("playbooks").delete().eq("id", deleteId);
      setRows(prev => prev.filter(r => r.id !== deleteId));
    } catch (e) {
      console.error(e);
      alert(t("playbooks:alerts.delete_failed"));
    } finally {
      setLoading(false);
      setDeleteId(null);
    }
  };

  if (loading) {
    return <LogoLoader />;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100">
      {/* ===== HEADER + FILTRES STICKY ===== */}
      <div className="sticky top-0 z-40 bg-gray-50/95 dark:bg-gray-950/85 backdrop-blur border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-6xl mx-auto px-4 py-3 pt-[calc(env(safe-area-inset-top,0px)+0.75rem)]">
          {/* Header */}
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <BackButton fallback="/dashboard" />

              <h1 className="text-xl sm:text-2xl font-semibold flex items-center gap-2">
                <Book className="w-6 h-6" />
                {t("playbooks:title")}
              </h1>
            </div>

            <Link
              to="/playbooks/new"
              className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-orange-500 text-white dark:bg-white dark:text-black"
            >
              <PlusCircle size={18} /> {t("playbooks:actions.new")}
            </Link>
          </div>

          {/* Filtres */}
          <div className="mt-3 flex flex-col md:flex-row gap-3 md:items-center">
            <div className="flex items-center gap-2 flex-1">
              <Search size={16} className="opacity-70" />
              <input
                className="w-full rounded-xl border px-3 py-2 bg-white dark:bg-zinc-950 dark:border-zinc-800"
                placeholder={t("playbooks:filters.search_placeholder")}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label={t("playbooks:filters.search_label")}
              />
            </div>

            <div className="flex items-center gap-2">
              <Filter size={16} className="opacity-70" />
              <select
                className="w-full md:w-auto rounded-xl border px-3 py-2 bg-white dark:bg-zinc-950 dark:border-zinc-800"
                value={teamFilter}
                onChange={(e) => setTeamFilter(e.target.value)}
                aria-label={t("playbooks:filters.team_label")}
              >
                <option value="">{t("playbooks:filters.all_teams")}</option>
                {teams.map((tt) => (
                  <option key={tt.id} value={tt.id}>
                    {tt.team_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* ===== CONTENU ===== */}
      <div className="max-w-6xl mx-auto px-4 py-4">
        {/* Empty state */}
        {filtered.length === 0 && (
          <div className="rounded-2xl border dark:border-zinc-800 p-8 text-center bg-white dark:bg-zinc-950">
            <div className="text-lg font-medium mb-1">{t("playbooks:empty.title")}</div>
            <div className="text-sm opacity-70 mb-4">{t("playbooks:empty.subtitle")}</div>
            <Link
              to="/playbooks/new"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-orange-500 text-white dark:bg-white dark:text-black"
            >
              <PlusCircle size={18} /> {t("playbooks:actions.new")}
            </Link>
          </div>
        )}

        {/* Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((pb) => (
            <div
              key={pb.id}
              className="rounded-2xl border dark:border-zinc-800 p-4 bg-white dark:bg-zinc-950 flex flex-col hover:shadow cursor-pointer transition"
              onClick={() => nav(`/playbooksdetails/${pb.id}`)}
              onKeyDown={(e) => {
                if (e.key === "Enter") nav(`/playbooksdetails/${pb.id}`);
              }}
              role="button"
              tabIndex={0}
            >
              <div className="flex-1">
                <div className="text-lg font-semibold">{pb.name}</div>
                <div className="text-sm opacity-70 mt-1">
                  {t("playbooks:card.team")} : {pb.team?.team_name ?? "—"}
                </div>
                {pb.description && (
                  <div className="text-sm opacity-80 mt-2 line-clamp-3">{pb.description}</div>
                )}
              </div>

              <div className="flex items-center justify-between mt-4">
                <div className="text-xs opacity-60">
                  {t("playbooks:card.created_on", { date: fmtDate(pb.created_at) })}
                </div>

                <div className="flex items-center gap-2">
                  <Link
                    to={`/playbooks/${pb.id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="px-2 py-1 rounded-lg border dark:border-zinc-800 inline-flex items-center gap-1"
                    title={t("playbooks:card.edit_title")}
                  >
                    <Pencil size={16} /> {t("playbooks:card.edit")}
                  </Link>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      confirmDelete(pb.id);
                    }}
                    className="px-2 py-1 rounded-lg border dark:border-zinc-800 inline-flex items-center gap-1"
                    title={t("playbooks:card.delete_title")}
                  >
                    <Trash2 size={16} /> {t("playbooks:card.delete")}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Modale de confirmation suppression */}
        {deleteId && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="w-[92%] max-w-md rounded-2xl bg-white dark:bg-zinc-950 border dark:border-zinc-800 p-4">
              <div className="text-lg font-semibold mb-2">{t("playbooks:modal.title")}</div>
              <div className="text-sm opacity-80">{t("playbooks:modal.body")}</div>
              <div className="flex justify-end gap-2 mt-4">
                <button
                  onClick={() => setDeleteId(null)}
                  className="px-3 py-2 rounded-xl border dark:border-zinc-800"
                >
                  {t("common:cancel")}
                </button>
                <button
                  onClick={doDelete}
                  className="px-3 py-2 rounded-xl bg-black text-white dark:bg-white dark:text-black"
                >
                  {t("playbooks:modal.confirm")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
